import React, { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Radio,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Button,
  Chip,
} from '@mui/material';
import withToast from '../../../../../hocs/withToast';

const riskCategories = [
  { label: 'Normal', value: 'normal', color: 'success' },
  { label: 'Borderline', value: 'borderline', color: 'orange' },
  { label: 'High', value: 'high', color: 'warning' },
  { label: 'Critical', value: 'critical', color: 'error' },
];

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 450,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

function AddThresholdModal({
  modalOpen,
  modalClose,
  modalHeader = 'Add Threshold',
  values = { min: '', max: '', type: '', vital: '', section: '' },
  onSave,
  disableRadio = false,
  notify
}) {
  const [thresholdMin, setThresholdMin] = useState(values.min);
  const [thresholdMax, setThresholdMax] = useState(values.max);
  const [selectedRisk, setSelectedRisk] = useState('');

  const isRiskTypeDisabled = (riskType) => {
    if (values.id && values.type && values.type.toLowerCase() === riskType.toLowerCase()) {
      return false;
    }

    // Check if this type already exists in the existingTypes array
    const existingTypes = values.existingTypes || [];
    return existingTypes.includes(riskType.toLowerCase());
  };

  // Get the first available risk type
  const getFirstAvailableRiskType = () => {
    // If editing, use the current type
    if (values.id && values.type) {
      return values.type;
    }

    // Find the first risk type that's not disabled
    for (const category of riskCategories) {
      if (!isRiskTypeDisabled(category.value)) {
        return category.value;
      }
    }

    // Default to normal if all are used (shouldn't happen as button should be disabled)
    return 'normal';
  };

  useEffect(() => {
    setThresholdMin(values.min || '');
    setThresholdMax(values.max || '');

    if (!values.id) {
      setSelectedRisk(getFirstAvailableRiskType());
    } else {
      // For edits, use the existing type
      setSelectedRisk(values.type || getFirstAvailableRiskType());
    }
  }, [values]);

  const handleRiskChange = (event) => {
    setSelectedRisk(event.target.value);
  };

  const handleSubmit = () => {
    if (!thresholdMin || !thresholdMax) {
      notify('Please fill in both minimum and maximum values', 'error');
      return;
    }

    const data = {
      min: thresholdMin,
      max: thresholdMax,
      type: selectedRisk,
      section: values.section
    };

    console.log('Submitted Threshold Data:', data);
    if (typeof onSave === 'function') {
      onSave(data);
    }

    modalClose();
  };

  return (
    <Modal open={modalOpen} onClose={modalClose}>
      <Box sx={style}>
        <Typography variant="h6" gutterBottom>
          {modalHeader}
        </Typography>

        <TextField
          label="Threshold Minimum"
          fullWidth
          margin="normal"
          type="number"
          value={thresholdMin}
          onChange={(e) => setThresholdMin(e.target.value)}
        />

        <TextField
          label="Threshold Maximum"
          fullWidth
          margin="normal"
          type="number"
          value={thresholdMax}
          onChange={(e) => setThresholdMax(e.target.value)}
        />

        <FormControl component="fieldset" sx={{ mt: 2 }}>
          <FormLabel component="legend">Risk Category Type</FormLabel>
          <RadioGroup value={selectedRisk} onChange={handleRiskChange}>
            {riskCategories.map((item) => (
              <FormControlLabel
                key={item.value}
                value={item.value}
                control={
                  <Radio
                    disabled={
                      disableRadio &&
                      isRiskTypeDisabled(item.value)
                    }
                  />
                }
                label={
                  <Chip
                    label={item.label}
                    color={item.color === 'orange' ? undefined : item.color}
                    sx={
                      item.color === 'orange'
                        ? {
                          backgroundColor: '#FF9800',
                          color: '#fff',
                          fontWeight: 'bold',
                        }
                        : { fontWeight: 'bold' }
                    }
                  />
                }
              />
            ))}
          </RadioGroup>
        </FormControl>

        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button variant="outlined" onClick={modalClose} sx={{ mr: 2 }}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            Submit
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

export default withToast(AddThresholdModal);